// index.tsx
import React, { useCallback, useEffect, useState } from "react";
import { Table } from "antd";
import { DatatableProps } from "../../data/interface";
import ConfirmationModal from "../../../feature-module/ers/common/components/ConfirmationModal";

const Datatable: React.FC<DatatableProps> = ({
  columns,
  dataSource,
  Selection,
  onFilterStatusChange,
  onBulkDelete,
  showPagination,
  selectedRowKeysParent,
}) => {
  const [selectedRowKeys, setSelectedRowKeys] = useState<any[]>([]);
  const [searchText, setSearchText] = useState<string>("");
  const [Selections, setSelections] = useState<any>(true);
  const [filteredDataSource, setFilteredDataSource] = useState(dataSource);

  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
  });

  const onSelectChange = (newSelectedRowKeys: any[]) => {
    setSelectedRowKeys(newSelectedRowKeys);
    selectedRowKeysParent(newSelectedRowKeys);
  };

  const handleBulkDelete = () => {
    if (onBulkDelete && selectedRowKeys.length > 0) {
      onBulkDelete(selectedRowKeys);
      setSelectedRowKeys([]);
      selectedRowKeysParent([]);
    }
  };

  useEffect(() => {
    // Notify parent when search is active or cleared
    if (onFilterStatusChange) {
      onFilterStatusChange(searchText.length > 0);
    }
  }, [searchText]);

  const handleSearch = (value: string) => {
    setSearchText(value);
    if (!Array.isArray(dataSource)) return;

    const filteredData = dataSource.filter((record) =>
      Object.values(record).some((field) =>
        String(field).toLowerCase().includes(value.toLowerCase())
      )
    );
    setFilteredDataSource(filteredData);
  };

  useEffect(() => {
    if (Array.isArray(dataSource)) {
      setFilteredDataSource(dataSource);
    }
  }, [dataSource]);

  const rowSelection = {
    selectedRowKeys,
    onChange: onSelectChange,
  };
  useEffect(() => {
    return setSelections(Selection);
  }, [Selection]);

  // Inject Sr.No column dynamically if needed
  const enhancedColumns = [
    {
      title: "Sr.No",
      render: (text: any, record: any, index: number) =>
        (pagination.current - 1) * pagination.pageSize + (index + 1),
    },
    ...columns,
  ];

  const commonTableProps = {
    className: "table datanew dataTable no-footer",
    columns: enhancedColumns,
    rowHoverable: false,
    dataSource: filteredDataSource,
    rowKey: "id",
    ...(showPagination && {
      pagination: {
        locale: { items_per_page: "" },
        nextIcon: <i className="ti ti-chevron-right" />,
        prevIcon: <i className="ti ti-chevron-left" />,
        current: pagination.current,
        pageSize: pagination.pageSize,
        defaultPageSize: 10,
        showSizeChanger: true,
        pageSizeOptions: ["10", "20", "30"],
        showTotal: (total: number, range: [number, number]) =>
          `Showing ${range[0]} - ${range[1]} of ${total} entries`,
        onChange: (page: number, pageSize: number) => {
          setPagination({ current: page, pageSize });
        },
      },
    }),
  };

  const [isBulkDeleteModalOpen, setIsBulkDeleteModalOpen] = useState(false);
  const handleBulkDeleteModalOpen = useCallback(() => {
    setIsBulkDeleteModalOpen(true);
  }, []);
  const handleCloseModal = useCallback(() => {
    setIsBulkDeleteModalOpen(false);
  }, []);

  return (
    <>
      <div className="table-top-data position-relative">
        <div className="row p-3">
          <div className="col-sm-12 col-md-6">
            <div
              className="dataTables_length"
              id="DataTables_Table_0_length"></div>
            {/* Show selection count when bulk delete is available and items are selected */}
          </div>
          <div className="col-sm-12 col-md-6">
            <div
              title="Search Records"
              id="DataTables_Table_0_filter"
              className="dataTables_filter text-end mb-0">
              <label>
                {" "}
                <input
                  type="search"
                  className="form-control form-control-sm"
                  placeholder="Search"
                  aria-controls="DataTables_Table_0"
                  value={searchText}
                  onChange={(e) => handleSearch(e.target.value)}
                />
              </label>
            </div>
          </div>
        </div>
        {/* Floating Bulk Delete Button */}
        {onBulkDelete && selectedRowKeys.length > 0 && (
          <div
            className="position-absolute d-flex align-items-center gap-3"
            style={{
              top: "20%",
              left: "35%",
              zIndex: 999,
              animation: "fadeInUp 0.3s ease",
            }}>
            <button
              className="btn btn-md btn-danger d-flex align-items-center"
              onClick={handleBulkDeleteModalOpen}
              title={`Delete ${selectedRowKeys.length} selected item${selectedRowKeys.length > 1 ? "s" : ""
                }`}>
              <i className="ti ti-trash me-2"></i>
              Bulk Delete ({selectedRowKeys.length})
            </button>

            <button
              className="btn btn-md btn-info d-flex align-items-center"
              onClick={() => setSelectedRowKeys([])}
              title={`Clear selection`}>
              <i className="ti ti-deselect me-2"></i>
              Clear
            </button>
          </div>
        )}
      </div>

      {!Selections ? (
        <Table {...commonTableProps} />
      ) : (
        <Table {...commonTableProps} rowSelection={rowSelection} />
      )}

      <ConfirmationModal
        isOpen={isBulkDeleteModalOpen}
        onClose={handleCloseModal}
        message="Are you sure you want to delete bulk items? This action cannot be undone."
        title="Delete Bulk Items"
        onConfirm={handleBulkDelete}
        type="danger"
      />
    </>
  );
};

export default Datatable;
